import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface FloorplanVariant {
  id: string;
  label: string;
  image: string;
  alt: string;
  size?: string;
}

export interface FloorplanTab {
  id: string;
  label: string;
  /** Single image tabs have one entry; grouped tabs have a/b/c variants */
  variants: FloorplanVariant[];
  /** Display label for the plan list block e.g. "GROUND LEVEL" */
  planLabel?: string;
}

@Component({
  selector: 'app-floorplans',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './floorplans.html',
  styleUrl: './floorplans.css'
})
export class FloorplansComponent {
  readonly tabs: FloorplanTab[] = [
    {
      id: '1bed',
      label: '1 Bedroom',
      variants: [
        { id: 'a', label: 'Type A', image: '/floorplans/1bedroom_a.png', alt: '1 Bedroom Type A floor plan', size: '60.28 m²' },
        { id: 'b', label: 'Type B', image: '/floorplans/1bedroom_b.png', alt: '1 Bedroom Type B floor plan', size: '66.13 m²' },
        { id: 'c', label: 'Type C', image: '/floorplans/1bedroom_c.png', alt: '1 Bedroom Type C floor plan', size: '59.16 m²' },
      ],
    },
    {
      id: '2bed',
      label: '2 Bedroom',
      variants: [
        { id: 'a', label: 'Type A', image: '/floorplans/2bedroom_a.png', alt: '2 Bedroom Type A floor plan', size: '95.74 m²' },
        { id: 'b', label: 'Type B', image: '/floorplans/2bedroom_b.png', alt: '2 Bedroom Type B floor plan', size: '97.43 m²' },
        { id: 'c', label: 'Type C', image: '/floorplans/2bedroom_c.png', alt: '2 Bedroom Type C floor plan', size: '99.05 m²' },
      ],
    },
    {
      id: 'ground',
      label: 'Ground',
      planLabel: 'GROUND LEVEL\nFLOOR PLAN',
      variants: [{ id: 'ground', label: 'Ground Level Floor Plan', image: '/floorplans/ground_new.webp', alt: 'Ground floor plan' }],
    },
    {
      id: 'mezzanine2',
      label: 'Mezzanine 2',
      planLabel: 'MEZZANINE 2\nFLOOR PLAN',
      variants: [{ id: 'mezzanine2', label: 'Mezzanine 2 Floor Plan', image: '/floorplans/mezanine2_new.webp', alt: 'Mezzanine 2 floor plan' }],
    },
    {
      id: 'first',
      label: 'First Floor',
      planLabel: 'FIRST FLOOR\nFLOOR PLAN',
      variants: [{ id: 'first', label: 'First Floor Plan', image: '/floorplans/first_floor.png', alt: 'First floor plan' }],
    },
    {
      id: 'typical',
      label: 'Typical',
      planLabel: 'TYPICAL\nFLOOR PLAN',
      variants: [{ id: 'typical', label: 'Typical Floor Plan', image: '/floorplans/typical.png', alt: 'Typical floor plan' }],
    },
    {
      id: 'roof',
      label: 'Rooftop',
      planLabel: 'ROOFTOP\nFLOOR PLAN',
      variants: [{ id: 'roof', label: 'Rooftop Floor Plan', image: '/floorplans/roof.webp', alt: 'Roof floor plan' }],
    },
  ];

  activeTabId = this.tabs[0].id;
  activeVariantId = this.tabs[0].variants[0].id;

  get activeTab(): FloorplanTab {
    return this.tabs.find(t => t.id === this.activeTabId)!;
  }

  get activeVariant(): FloorplanVariant {
    return this.activeTab.variants.find(v => v.id === this.activeVariantId)
      ?? this.activeTab.variants[0];
  }

  get hasVariants(): boolean {
    return this.activeTab.variants.length > 1;
  }

  selectTab(tabId: string): void {
    this.activeTabId = tabId;
    this.activeVariantId = this.tabs.find(t => t.id === tabId)!.variants[0].id;
  }
}
